import { LocalCliError } from "./errors.js";

/** The public GHCR repo the platform-update path tracks by default. */
export const DEFAULT_IMAGE_REPO = "ghcr.io/m8t-labs/m8t";

export interface ParsedImageRef { registry: string; repo: string; tag: string | undefined }

/**
 * Split "ghcr.io/m8t-labs/m8t:v0.1.0" into registry/repo/tag. The tag is the
 * segment after the last ':' only when that ':' follows the last '/', so a
 * registry port (host:5000/repo) is not mistaken for a tag.
 */
export function parseImageRef(ref: string): ParsedImageRef {
  const lastColon = ref.lastIndexOf(":");
  const lastSlash = ref.lastIndexOf("/");
  let name = ref;
  let tag: string | undefined;
  if (lastColon > lastSlash) {
    name = ref.slice(0, lastColon);
    tag = ref.slice(lastColon + 1) || undefined;
  }
  return { registry: name.split("/")[0], repo: name, tag };
}

const SEMVER_TAG = /^v(\d+)\.(\d+)\.(\d+)$/;

export function isSemverTag(tag: string): boolean {
  return SEMVER_TAG.test(tag);
}

export function parseSemver(tag: string): [number, number, number] | null {
  const m = SEMVER_TAG.exec(tag);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** -1 if a<b, 0 if equal, 1 if a>b. Both must be vX.Y.Z. */
export function compareSemver(a: string, b: string): number {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (!pa || !pb) {
    throw new LocalCliError({
      code: "PLATFORM_BAD_SEMVER",
      message: `compareSemver needs vX.Y.Z tags, got '${a}' / '${b}'.`,
    });
  }
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] < pb[i] ? -1 : 1;
  }
  return 0;
}

/** The highest vX.Y.Z tag, or null when none of `tags` is a semver tag. */
export function pickNewestSemver(tags: string[]): string | null {
  const semver = tags.filter(isSemverTag);
  if (semver.length === 0) return null;
  return semver.reduce((best, t) => (compareSemver(t, best) > 0 ? t : best));
}

export interface ParsedResourceId { subscriptionId: string; resourceGroup: string; name: string }

export function parseContainerAppResourceId(id: string): ParsedResourceId {
  const m =
    /\/subscriptions\/([^/]+)\/resourceGroups\/([^/]+)\/providers\/Microsoft\.App\/containerApps\/([^/]+)/i.exec(
      id,
    );
  if (!m) {
    throw new LocalCliError({
      code: "PLATFORM_BAD_RESOURCE_ID",
      message: `Could not parse a Container App resource id: ${id}`,
      hint: "Re-run discovery with 'm8t whoami', or pass --subscription.",
    });
  }
  return { subscriptionId: m[1], resourceGroup: m[2], name: m[3] };
}

export type UpdatePlan =
  | { kind: "noop"; current: string; available: string }
  | { kind: "roll"; current: string; available: string; target: string; repo: string }
  | { kind: "no-versions"; current: string }
  | { kind: "refuse-byoc"; current: string; currentRepo: string; trackedRepo: string }
  | { kind: "tag-not-found"; current: string; requested: string };

/**
 * Pure decision: given the live image, the available GHCR tags, the tracked
 * repo, and an optional explicit `--to`, decide what `m8t platform update`
 * should do. Auto mode never downgrades; an explicit `--to` may roll backward
 * (deliberate rollback). A non-semver current (`:latest`/SHA) always rolls onto
 * the pinned version. A different repo (BYOC private ACR) is refused.
 */
export function planUpdate(opts: {
  currentImage: string;
  availableTags: string[];
  imageRepo: string;
  to?: string;
}): UpdatePlan {
  const cur = parseImageRef(opts.currentImage);
  // Display-only sentinel: a deployed Container App image always carries a tag,
  // and `currentTag` is never used as a roll target (that's always `target`).
  const currentTag = cur.tag ?? "(none)";

  if (cur.repo !== opts.imageRepo) {
    return { kind: "refuse-byoc", current: currentTag, currentRepo: cur.repo, trackedRepo: opts.imageRepo };
  }

  if (opts.to && !opts.availableTags.includes(opts.to)) {
    return { kind: "tag-not-found", current: currentTag, requested: opts.to };
  }

  const available = opts.to ?? pickNewestSemver(opts.availableTags);
  if (!available) return { kind: "no-versions", current: currentTag };

  if (cur.tag === available) return { kind: "noop", current: currentTag, available };

  // Auto mode (no --to): only roll when strictly newer, or when the current tag
  // is not a tracked semver (e.g. :latest / a SHA), which we migrate onto a pin.
  if (!opts.to) {
    const currentIsSemver = cur.tag ? isSemverTag(cur.tag) : false;
    // Safe: `available` came from pickNewestSemver (always a semver tag) and
    // currentIsSemver guards cur.tag — so compareSemver never sees a non-semver.
    // cur.tag is guaranteed non-null when currentIsSemver is true (checked above)
    if (currentIsSemver && cur.tag && compareSemver(available, cur.tag) <= 0) {
      return { kind: "noop", current: currentTag, available };
    }
  }
  return { kind: "roll", current: currentTag, available, target: available, repo: opts.imageRepo };
}

export function summarizePlan(plan: UpdatePlan): { current: string; available: string; verdict: string } {
  switch (plan.kind) {
    case "noop":
      return { current: plan.current, available: plan.available, verdict: "up to date" };
    case "roll":
      return { current: plan.current, available: plan.available, verdict: "update available" };
    case "no-versions":
      return { current: plan.current, available: "-", verdict: "no published versions found" };
    case "refuse-byoc":
      return { current: plan.current, available: "-", verdict: `private-ACR image (${plan.currentRepo}) — not tracked` };
    case "tag-not-found":
      return { current: plan.current, available: plan.requested, verdict: `tag ${plan.requested} not published` };
  }
}

/**
 * Anonymously list tags for a PUBLIC GHCR repo. Needs no GitHub auth and no
 * docker — just two HTTPS GETs (token exchange, then the v2 tags list). repo is
 * "ghcr.io/<owner>/<name>". `fetchImpl` is injectable for tests.
 */
export async function fetchPublicTags(
  repo: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string[]> {
  if (!repo.startsWith("ghcr.io/")) {
    throw new LocalCliError({
      code: "PLATFORM_NOT_GHCR",
      message: `fetchPublicTags only supports ghcr.io repos, got ${repo}.`,
      hint: "Pass a ghcr.io/<owner>/<name> repo, or use the BYOC private-ACR deploy flow.",
    });
  }
  const path = repo.replace(/^ghcr\.io\//, ""); // "m8t-labs/m8t"
  const tokenRes = await fetchImpl(`https://ghcr.io/token?scope=repository:${path}:pull`);
  if (!tokenRes.ok) {
    throw new LocalCliError({
      code: "PLATFORM_GHCR_TOKEN_FAILED",
      message: `Could not get an anonymous GHCR token for ${repo} (HTTP ${tokenRes.status.toString()}).`,
      hint: "If the package is still private, ask the operator to make it public (deploy/ghcr-package-visibility.md).",
    });
  }
  const { token } = (await tokenRes.json()) as { token?: string };
  if (!token) {
    throw new LocalCliError({
      code: "PLATFORM_GHCR_PRIVATE",
      message: `GHCR returned no anonymous token for ${repo} — the package is likely still private.`,
      hint: "Make the package public (deploy/ghcr-package-visibility.md), then retry.",
    });
  }
  const tagsRes = await fetchImpl(`https://ghcr.io/v2/${path}/tags/list`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!tagsRes.ok) {
    throw new LocalCliError({
      code: "PLATFORM_GHCR_TAGS_FAILED",
      message: `Could not list tags for ${repo} (HTTP ${tagsRes.status.toString()}).`,
    });
  }
  const body = (await tagsRes.json()) as { tags?: string[] };
  return body.tags ?? [];
}
